import { useEffect, useState } from 'react'
import './CopyButton.css'

/**
 * Copies a registration number to the clipboard.
 *
 * These get typed into VAHAN, into insurer portals and into WhatsApp messages all
 * day, and retyping MH12EQ3823 by eye is exactly where a transposed digit gets
 * introduced. In a compliance register a wrong registration is worse than none.
 *
 * Copies the CANONICAL form, not the spelling on the paperwork: MH12EQ3823 is
 * what every portal expects, whereas "MH 12 EQ 3823" fails the lookup.
 */
export function CopyButton({ value, label }: { value: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')

  useEffect(() => {
    if (state === 'idle') return
    const timer = setTimeout(() => setState('idle'), 1400)
    return () => clearTimeout(timer)
  }, [state])

  async function copy() {
    try {
      // navigator.clipboard is undefined outside a secure context. localhost
      // counts as one, so this is only ever hit if the app is served over plain
      // HTTP — which the fallback covers rather than failing silently.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
      } else if (!legacyCopy(value)) {
        throw new Error('copy rejected')
      }

      setState('copied')
    } catch {
      // Reported, not swallowed: the user is about to paste, and believing they
      // have the number when they do not is the failure that matters.
      setState('failed')
    }
  }

  return (
    <button
      type="button"
      className={`copybtn copybtn--${state}`}
      // The row navigates to the vehicle; copying must not also open it.
      onClick={(e) => {
        e.stopPropagation()
        void copy()
      }}
      title={
        state === 'copied'
          ? `Copied ${value}`
          : state === 'failed'
            ? 'Could not copy — select the text instead'
            : `Copy ${label}`
      }
      aria-label={`Copy ${label} ${value}`}
    >
      {state === 'copied' ? '✓' : state === 'failed' ? '!' : <CopyIcon />}
      {/* Announced to screen readers, which get no benefit from the tick alone.
          aria-live so the confirmation is read when it appears. */}
      <span className="visually-hidden" aria-live="polite">
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : ''}
      </span>
    </button>
  )
}

/**
 * Pre-clipboard-API fallback: a hidden textarea plus execCommand.
 *
 * Deprecated, and kept anyway — it is the only thing that works when the app is
 * reached over plain HTTP, which is a realistic way for this to be deployed on
 * an internal network.
 */
function legacyCopy(value: string): boolean {
  const area = document.createElement('textarea')
  area.value = value
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.opacity = '0'
  document.body.appendChild(area)

  try {
    area.select()
    return document.execCommand('copy')
  } catch {
    return false
  } finally {
    document.body.removeChild(area)
  }
}

function CopyIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false">
      <rect x="3.6" y="3.6" width="7.4" height="7.4" rx="1.2" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M8.4 3.6V2.2A1.2 1.2 0 0 0 7.2 1H2.2A1.2 1.2 0 0 0 1 2.2v5a1.2 1.2 0 0 0 1.2 1.2h1.4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  )
}
